import {FC, useEffect, useRef, useState} from 'react'
import {Button} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import SharedData from '../../common/SharedData'
import Loader from '../../common/Loader'
import ProductThumb from '../../../components/common/ProductThumb'
import {toCatalogImageUrl} from '../../../../_metronic/helpers'
import {IOfferModel} from '../../offers/cors/_models'
import {createStockRequest, searchOwnOffers} from '../core/_requests'
import {
  ISelectedStockItem,
  STOCK_REQUEST_MAX_ITEMS,
  STOCK_REQUEST_MAX_QUANTITY,
  STOCK_REQUEST_MIN_QUANTITY,
  isValidStockQuantity,
} from '../core/_models'
import {directionClasses} from '../core/direction'
import {extractErrorMessage, extractItemErrors} from '../core/errors'

const PAGE_SIZE = 10
const SEARCH_DEBOUNCE_MS = 350

type Props = {
  onSubmitted: () => void
}

const NewStockRequest: FC<Props> = ({onSubmitted}) => {
  const {formatMessage} = useIntl()
  const {currentUser, language} = SharedData()
  const dir = directionClasses(language)
  const token = currentUser?.token
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [offers, setOffers] = useState<IOfferModel[]>([])
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [selected, setSelected] = useState<ISelectedStockItem[]>([])
  const [submitting, setSubmitting] = useState(false)
  const requestSeq = useRef(0)

  useEffect(() => {
    const term = searchInput.trim()
    if (term === search) return
    const handle = window.setTimeout(() => {
      setSearch(term)
      setPage(1)
    }, SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(handle)
  }, [searchInput, search])

  useEffect(() => {
    const seq = ++requestSeq.current
    setLoading(true)
    setLoadError(null)
    searchOwnOffers({search, page, limit: PAGE_SIZE}, token, language)
      .then((res) => {
        if (seq !== requestSeq.current) return
        const data = res.data
        setOffers(data?.data ?? [])
        setTotalPages(data?.pages ?? Math.ceil((data?.total ?? 0) / PAGE_SIZE))
      })
      .catch((err) => {
        if (seq !== requestSeq.current) return
        setOffers([])
        setTotalPages(0)
        setLoadError(
          extractErrorMessage(err) || formatMessage({id: 'add_to_stock.load_products_error'})
        )
      })
      .finally(() => {
        if (seq === requestSeq.current) setLoading(false)
      })
  }, [search, page, token, language, formatMessage])

  const selectedIds = new Set(selected.map((item) => item.offer.product_supplier_id))
  const limitReached = selected.length >= STOCK_REQUEST_MAX_ITEMS

  const toggleOffer = (offer: IOfferModel) => {
    // The server numbers its row errors by position in what was sent, and they are shown by
    // position in this list, so the list does not change while a request is on its way.
    if (submitting) return
    if (selectedIds.has(offer.product_supplier_id)) {
      removeItem(offer.product_supplier_id)
      return
    }
    if (limitReached) {
      toast.warning(formatMessage({id: 'add_to_stock.max_items'}, {max: STOCK_REQUEST_MAX_ITEMS}))
      return
    }
    setSelected((prev) => [...prev, {offer, quantity: '', touched: false, serverError: null}])
  }

  const removeItem = (productSupplierId: number) => {
    setSelected((prev) =>
      prev.filter((item) => item.offer.product_supplier_id !== productSupplierId)
    )
  }

  const updateItem = (productSupplierId: number, patch: Partial<ISelectedStockItem>) => {
    setSelected((prev) =>
      prev.map((item) =>
        item.offer.product_supplier_id === productSupplierId ? {...item, ...patch} : item
      )
    )
  }

  const quantityError = (item: ISelectedStockItem): string | null => {
    if (item.serverError) return item.serverError
    if (item.quantity === '' && !item.touched) return null
    return isValidStockQuantity(item.quantity)
      ? null
      : formatMessage(
          {id: 'add_to_stock.quantity_invalid'},
          {min: STOCK_REQUEST_MIN_QUANTITY, max: STOCK_REQUEST_MAX_QUANTITY}
        )
  }

  const allValid =
    selected.length > 0 &&
    selected.length <= STOCK_REQUEST_MAX_ITEMS &&
    selected.every((item) => isValidStockQuantity(item.quantity) && !item.serverError)

  const handleSubmit = async () => {
    if (!allValid || submitting) return
    setSubmitting(true)
    const items = selected.map((item) => ({
      product_supplier_id: item.offer.product_supplier_id,
      quantity: Number(item.quantity),
    }))
    try {
      const res = await createStockRequest({items}, token, language)
      toast.success(res.data?.message || formatMessage({id: 'add_to_stock.success'}))
      setSelected([])
      onSubmitted()
    } catch (err) {
      toast.error(extractErrorMessage(err) || formatMessage({id: 'something_wrong_desc'}))
      const rowErrors = extractItemErrors(err)
      if (Object.keys(rowErrors).length > 0) {
        setSelected((prev) =>
          prev.map((item, index) =>
            rowErrors[index] ? {...item, touched: true, serverError: rowErrors[index]} : item
          )
        )
      }
    } finally {
      setSubmitting(false)
    }
  }

  const renderResults = () => {
    if (loading) return <Loader />
    if (loadError) return <div className='text-danger text-center py-8'>{loadError}</div>
    if (offers.length === 0) {
      return (
        <div className='text-muted text-center py-8'>
          {formatMessage({id: 'add_to_stock.no_products'})}
        </div>
      )
    }
    return (
      <>
        <div className='table-responsive'>
          <table className='table table-row-bordered table-row-gray-200 align-middle gy-3'>
            <thead>
              <tr className='fw-bold text-muted bg-light'>
                <th className={`${dir.padStart} ${dir.roundStart} w-50px`}></th>
                <th className='w-100px text-center'>{formatMessage({id: 'image'})}</th>
                <th className={`${dir.start}`}>{formatMessage({id: 'prod_name'})}</th>
                <th className={`${dir.start}`}>{formatMessage({id: 'barcode'})}</th>
                <th className='text-center'>
                  {formatMessage({id: 'add_to_stock.supplier_stock'})}
                </th>
                <th className={`text-center ${dir.padEnd} ${dir.roundEnd}`}>
                  {formatMessage({id: 'add_to_stock.warehouse_stock'})}
                </th>
              </tr>
            </thead>
            <tbody>
              {offers.map((offer) => {
                const isSelected = selectedIds.has(offer.product_supplier_id)
                const name = offer.product_name ?? `#${offer.product_id}`
                return (
                  <tr
                    key={offer.product_supplier_id}
                    className={`cursor-pointer${isSelected ? ' bg-light-primary' : ''}`}
                    onClick={() => toggleOffer(offer)}
                  >
                    <td className={`${dir.padStart}`} onClick={(e) => e.stopPropagation()}>
                      <div className='form-check form-check-sm form-check-custom'>
                        <input
                          type='checkbox'
                          className='form-check-input'
                          checked={isSelected}
                          disabled={submitting || (!isSelected && limitReached)}
                          aria-label={formatMessage({id: 'add_to_stock.select_product'}, {name})}
                          onChange={() => toggleOffer(offer)}
                        />
                      </div>
                    </td>
                    <td className='text-center'>
                      <ProductThumb
                        src={toCatalogImageUrl(offer.image)}
                        alt={name}
                        loading='lazy'
                      />
                    </td>
                    <td className={`${dir.start} fw-semibold`}>{name}</td>
                    <td className={`${dir.start} text-nowrap`}>{offer.barcode || '—'}</td>
                    <td className='text-center'>{offer.supplier_quantity ?? 0}</td>
                    <td className={`text-center ${dir.padEnd}`}>{offer.warehouse_quantity ?? 0}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className='d-flex justify-content-end align-items-center gap-3 mt-2'>
            <Button
              variant='light'
              size='sm'
              aria-label={formatMessage({id: 'previous'})}
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ‹
            </Button>
            <span className='text-muted'>
              {page} / {totalPages}
            </span>
            <Button
              variant='light'
              size='sm'
              aria-label={formatMessage({id: 'next'})}
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              ›
            </Button>
          </div>
        )}
      </>
    )
  }

  const renderSelected = () => {
    if (selected.length === 0) {
      return (
        <div className='text-muted text-center py-6'>
          {formatMessage({id: 'add_to_stock.no_selection'})}
        </div>
      )
    }
    return (
      <div className='table-responsive'>
        <table className='table table-row-bordered table-row-gray-200 align-middle gy-3'>
          <thead>
            <tr className='fw-bold text-muted bg-light'>
              <th className={`${dir.padStart} ${dir.roundStart} w-100px text-center`}>
                {formatMessage({id: 'image'})}
              </th>
              <th className={`${dir.start}`}>{formatMessage({id: 'prod_name'})}</th>
              <th className={`${dir.start}`}>{formatMessage({id: 'barcode'})}</th>
              <th className={`${dir.start} w-200px`}>{formatMessage({id: 'quantity'})}</th>
              <th className={`${dir.end} ${dir.padEnd} ${dir.roundEnd}`}></th>
            </tr>
          </thead>
          <tbody>
            {selected.map((item) => {
              const {offer} = item
              const id = offer.product_supplier_id
              const name = offer.product_name ?? `#${offer.product_id}`
              const error = quantityError(item)
              return (
                <tr key={id}>
                  <td className={`${dir.padStart} text-center`}>
                    <ProductThumb src={toCatalogImageUrl(offer.image)} alt={name} loading='lazy' />
                  </td>
                  <td className={`${dir.start} fw-semibold`}>{name}</td>
                  <td className={`${dir.start} text-nowrap`}>{offer.barcode || '—'}</td>
                  <td className={`${dir.start}`}>
                    <input
                      type='number'
                      inputMode='numeric'
                      min={STOCK_REQUEST_MIN_QUANTITY}
                      max={STOCK_REQUEST_MAX_QUANTITY}
                      step={1}
                      className={`form-control form-control-sm${error ? ' is-invalid' : ''}`}
                      aria-label={`${formatMessage({id: 'quantity'})} - ${name}`}
                      aria-invalid={!!error}
                      value={item.quantity}
                      disabled={submitting}
                      onChange={(e) =>
                        updateItem(id, {quantity: e.target.value, serverError: null})
                      }
                      onBlur={() => updateItem(id, {touched: true})}
                    />
                    {error && <div className='invalid-feedback d-block'>{error}</div>}
                  </td>
                  <td className={`${dir.end} ${dir.padEnd}`}>
                    <button
                      type='button'
                      className='btn btn-sm btn-icon btn-light-danger'
                      title={formatMessage({id: 'remove'})}
                      aria-label={`${formatMessage({id: 'remove'})} - ${name}`}
                      disabled={submitting}
                      onClick={() => removeItem(id)}
                    >
                      <i className='fas fa-trash-alt' />
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <div className='card mb-5 mb-xl-8'>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>
            {formatMessage({id: 'add_to_stock.new_request.title'})}
          </span>
          <span className='text-muted mt-1 fw-semibold fs-7'>
            {formatMessage({id: 'add_to_stock.new_request.subtitle'})}
          </span>
        </h3>
        <div className='card-toolbar'>
          <input
            type='search'
            className='form-control form-control-sm form-control-solid w-250px'
            placeholder={formatMessage({id: 'add_to_stock.search_placeholder'})}
            aria-label={formatMessage({id: 'search'})}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
      </div>
      <div className='card-body py-4'>
        {renderResults()}

        <div className='separator my-6'></div>

        <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4'>
          <div className='d-flex align-items-center gap-3'>
            <h4 className='fw-bold mb-0'>
              {formatMessage({id: 'add_to_stock.selected_products'})}
            </h4>
            <span className='badge badge-light-primary'>
              {formatMessage({id: 'selected'})}: {selected.length}
            </span>
          </div>
        </div>

        {renderSelected()}

        <div className='d-flex justify-content-end mt-6'>
          <button
            type='button'
            className='btn btn-primary'
            disabled={!allValid || submitting}
            onClick={handleSubmit}
          >
            {submitting ? (
              <>
                <span
                  className={`spinner-border spinner-border-sm align-middle ${dir.marginEnd}`}
                ></span>
                {formatMessage({id: 'add_to_stock.submitting'})}
              </>
            ) : (
              formatMessage({id: 'add_to_stock.submit'})
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default NewStockRequest
